import { TFeatureWithPrecision } from '../../../../../types/map';

export type TOfferPreviewBalloonPosition = 'top' | 'bottom' | 'right' | 'left';

// Зоны по краям карты, занятые контролами/оверлеями: балун в них не заходит при выборе стороны.
// Значения приходят извне (из зоны Map - владельца контролов); сама утилита их не знает.
export interface ISafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

// Отступ превью от пина по вертикали - отдельно для позиций сверху и снизу
const BALLOON_PIN_GAP_TOP = 3;
const BALLOON_PIN_GAP_BOTTOM = 31;

// Отступ балуна от пина по горизонтали (для left/right) - увеличен вдвое
const BALLOON_PIN_GAP_HORIZONTAL = 48;

// Порядок предпочтения сторон: первая подходящая выигрывает
const POSITION_PRIORITY: TOfferPreviewBalloonPosition[] = ['top', 'bottom', 'right', 'left'];

// Сдвиг левого-верхнего угла балуна относительно пина для каждой стороны (без учёта gap).
// previewHeight приблизительная - нужна только для эвристики выбора стороны; точное вертикальное
// смещение задаётся в CSS процентами (см. getBalloonOffsetY).
function getCornerOffset(
  position: TOfferPreviewBalloonPosition,
  previewWidth: number,
  previewHeight: number,
): readonly [number, number] {
  switch (position) {
    case 'top':
      return [-previewWidth / 2, -previewHeight];
    case 'bottom':
      return [-previewWidth / 2, 0];
    case 'left':
      return [-previewWidth, -previewHeight / 2];
    case 'right':
    default:
      return [0, -previewHeight / 2];
  }
}

// Сумма выходов балуна за границы доступной зоны (карта минус safeArea). 0 - целиком влезает.
function getOverflow(
  position: TOfferPreviewBalloonPosition,
  pinX: number,
  pinY: number,
  mapWidth: number,
  mapHeight: number,
  safeArea: ISafeAreaInsets,
  previewWidth: number,
  previewHeight: number,
): number {
  const [dx, dy] = getCornerOffset(position, previewWidth, previewHeight);
  const left = pinX + dx;
  const top = pinY + dy;

  return (
    Math.max(safeArea.left - left, 0) +
    Math.max(left + previewWidth - (mapWidth - safeArea.right), 0) +
    Math.max(safeArea.top - top, 0) +
    Math.max(top + previewHeight - (mapHeight - safeArea.bottom), 0)
  );
}

// Выбирает сторону пина, где балун меньше всего вылезает за доступную зону карты.
// pinCenterOffsetX - сдвиг опорной точки к визуальному центру пилюли (для боковых anchor
// гео-точка фичи лежит на краю пилюли, а не в центре; см. getPinCenterOffsetX).
// safeArea - зоны под контролами, которые балун обходит при выборе стороны.
// previewWidth/previewHeight - размеры превью (обычный/компактный), приходят извне.
export function getBalloonPosition(
  map: YMaps.Map,
  feature: TFeatureWithPrecision,
  pinCenterOffsetX: number,
  safeArea: ISafeAreaInsets,
  previewWidth: number,
  previewHeight: number,
): TOfferPreviewBalloonPosition {
  // Размеры контейнера карты в пикселях
  const mapRect = map.container.getParentElement().getBoundingClientRect();

  // Переводим географические координаты пина и центра карты в пиксели
  const projection: YMaps.projection.wgs84Mercator = map.options.get('projection');
  const zoom = map.getZoom();
  const featurePixel = projection.toGlobalPixels(feature.geometry.coordinates as YMaps.TCoord, zoom);
  const centerPixel = projection.toGlobalPixels(map.getCenter(), zoom);

  // Экранные координаты визуального центра пилюли относительно левого-верхнего угла карты
  const pinX = mapRect.width / 2 + featurePixel[0] - centerPixel[0] + pinCenterOffsetX;
  const pinY = mapRect.height / 2 + featurePixel[1] - centerPixel[1];

  // Перебираем стороны по приоритету и берём ту, где балун меньше всего вылезает за зону
  return POSITION_PRIORITY.reduce((best, position) => {
    return getOverflow(position, pinX, pinY, mapRect.width, mapRect.height, safeArea, previewWidth, previewHeight) <
      getOverflow(best, pinX, pinY, mapRect.width, mapRect.height, safeArea, previewWidth, previewHeight)
      ? position
      : best;
  }, POSITION_PRIORITY[0]);
}

// Горизонтальное смещение балуна (px) от пина для стороны (зависит от ширины превью).
function getBalloonOffsetX(position: TOfferPreviewBalloonPosition, previewWidth: number): number {
  switch (position) {
    case 'right':
      return BALLOON_PIN_GAP_HORIZONTAL;
    case 'left':
      return -previewWidth - BALLOON_PIN_GAP_HORIZONTAL;
    case 'top':
    case 'bottom':
    default:
      return -previewWidth / 2;
  }
}

// Вертикальное смещение в CSS-единицах: проценты браузер считает от РЕАЛЬНОЙ высоты балуна,
// поэтому позиция не зависит от статической высоты превью. Пробелы вокруг минуса в calc обязательны.
function getBalloonOffsetY(position: TOfferPreviewBalloonPosition): string {
  switch (position) {
    case 'bottom':
      return `${BALLOON_PIN_GAP_BOTTOM}px`;
    case 'right':
    case 'left':
      return '-50%';
    case 'top':
    default:
      return `calc(-100% - ${BALLOON_PIN_GAP_TOP}px)`;
  }
}

// CSS transform балуна от пина для выбранной стороны: X в px, Y в процентах/calc от реальной высоты.
// pinCenterOffsetX сдвигает балун к визуальному центру пилюли (как в getBalloonPosition).
export function getBalloonTransform(
  position: TOfferPreviewBalloonPosition,
  pinCenterOffsetX: number,
  previewWidth: number,
): string {
  const x = getBalloonOffsetX(position, previewWidth) + pinCenterOffsetX;

  return `translate(${x}px, ${getBalloonOffsetY(position)})`;
}
